import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import LoaderWithFade from 'components/common/LoaderWithFade/LoaderWithFade';
import useQuery from 'hooks/generic/useQuery';

import styles from './SharedLesson.module.scss';

type Status = 'loading' | 'ready' | 'error';

// Pull the server-provided filename out of the Content-Disposition header so the
// downloaded PDF keeps the lesson's title; fall back to a generic name.
const filenameFromDisposition = (disposition: string | null, fallback: string) => {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Public, no-login page opened from a shared-lesson link. It downloads the
 * lesson PDF from the backend (the link carries a signed token) and saves it.
 */
const SharedLesson: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const query = useQuery();
  const token = query.get('token') || '';
  const [status, setStatus] = useState<Status>('loading');

  const download = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch(
        `${window.ENV.REACT_APP_BACKEND_URL}shared/lesson/${id}/pdf?token=${encodeURIComponent(
          token
        )}`
      );
      if (!response.ok) throw new Error('Shared lesson download failed');

      const blob = await response.blob();
      const filename = filenameFromDisposition(
        response.headers.get('content-disposition'),
        `${t('editor:lesson', 'Lesson')}.pdf`
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setStatus('ready');
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  }, [id, token, t]);

  // Trigger the download once when the page opens. Browsers that block the
  // automatic download still get the manual button below.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    download();
  }, [download]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {status === 'loading' && (
          <>
            <LoaderWithFade />
            <p className={styles.message}>
              {t('sharedLesson:preparing', 'Preparing your lesson download…')}
            </p>
          </>
        )}

        {status === 'ready' && (
          <>
            <p className={styles.message}>
              {t(
                'sharedLesson:ready',
                'Your download should start automatically. If it does not, use the button below.'
              )}
            </p>
            <button className={styles.button} onClick={download}>
              {t('sharedLesson:download', 'Download lesson')}
            </button>
          </>
        )}

        {status === 'error' && (
          <p className={styles.message}>
            {t('sharedLesson:invalid', 'This link is invalid or has expired.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default SharedLesson;
